import { useRef, useState } from "react";
import { Clock, LoaderCircle, Mail, MessageCircle } from "lucide-react";
import { preventivoApi, type Vista } from "@/api/preventivo";
import { linkApi } from "@/api/linkPreventivo";
import { ApiError } from "@/api/client";
import { bolletteInLettura, condominiDelLink, dataBreve } from "@/lib/linkPreventivo";
import { indirizzoWhatsApp } from "@/lib/whatsapp";
import { PannelloBasso } from "@/components/ui/PannelloBasso";
import { AvvisoErrore } from "./BolletteFuori";
import { Invii } from "./Invii";
import { COLLEGAMENTO_NON_RIUSCITO, fornitureN } from "./scritte";
import { ancoraInLettura } from "./scritteLink";
import {
  INVIA_AMMINISTRATORE, MAIL, MAIL_NON_PARTITA, MANCA_EMAIL, METTI_TUTTI, PRIMA_SALVA, TOGLI_TUTTI, WHATSAPP,
  condominiNelLink, laMailVaA, rigaInvio, testoWhatsApp,
} from "./scritteLista";
import { FOCUS } from "./parti";

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  pid: string;
  vista: Vista;
  /** I condomìni tolti dal link, tenuti dalla pagina: gli altri sono spuntati, così si parte da tutti. */
  tolti: Set<string>;
  onScambia: (chiave: string) => void;
  /** «Togli tutti» (false) e «Metti tutti» (true). */
  onTutti: (metti: boolean) => void;
  /** Il server ha rifiutato perché la pagina era rimasta indietro (letture in corso): si ricarica. */
  onRifiutato: () => void;
  /** La vista che torna dopo un invio: ha l'invio nuovo nel registro. */
  onVista: (v: Vista) => void;
  /** Il riquadro Amministratore ha un salvataggio in corso o un valore non salvato: la mail partirebbe all'indirizzo vecchio.
   *  I tasti restano fermi e sopra c'è «Prima salva i dati dell'amministratore.». */
  bloccato?: boolean;
}

type Canale = "mail" | "whatsapp";

const BASE = `inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg px-4 text-base font-semibold cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS}`;
const PRIMARIO = `${BASE} bg-brand-dark text-white enabled:hover:bg-dark focus-visible:ring-offset-2`;
const SECONDARIO = `${BASE} border border-border bg-card text-text enabled:hover:bg-surface`;

/** Il pannello «Invia all'amministratore» (mockup invio-finale, 29/10): si apre da «Invia» nella barra del telefono o dal
 *  riquadro in cima dal computer. Scorre solo la lista dei condomìni; Mail e WhatsApp restano fermi in fondo. «Mail»: il
 *  portale crea il link e spedisce; «WhatsApp»: crea il link e apre WhatsApp col messaggio scritto (che parta lo sa solo
 *  il telefono). A letture finite e con almeno un condominio spuntato; un invio alla volta. */
export function InvioAmministratore({ aperto, onChiudi, pid, vista, tolti, onScambia, onTutti, onRifiutato, onVista, bloccato = false }: Props) {
  const [inCorso, setInCorso] = useState<Canale | null>(null);
  const [errore, setErrore] = useState<Canale | null>(null);
  const inVolo = useRef(false);

  const elenco = condominiDelLink(vista);
  const mancano = bolletteInLettura(vista);
  const scelti = elenco.map((c) => c.chiave).filter((k) => !tolti.has(k));
  const email = vista.amministratore?.email ?? null;
  const pronto = mancano === 0 && scelti.length > 0 && inCorso === null && !bloccato;
  const invii = vista.invii ?? [];
  const ultimo = invii.length > 0 ? invii.reduce((a, b) => (Date.parse(b.il) > Date.parse(a.il) ? b : a)) : null;

  async function manda(canale: Canale) {
    if (inVolo.current) return;
    if (canale === "mail" && email === null) return;
    inVolo.current = true;
    setInCorso(canale);
    setErrore(null);
    try {
      if (canale === "mail") {
        onVista(await preventivoApi.mandaMail(pid, scelti, email as string));
      } else {
        const r = await preventivoApi.whatsapp(pid, scelti);
        onVista(r.vista);
        const testo = testoWhatsApp(scelti.length, dataBreve(r.scade_il), linkApi.indirizzo(r.token));
        window.location.href = indirizzoWhatsApp(r.vista.amministratore?.cellulare ?? vista.amministratore?.cellulare ?? null, testo);
      }
    } catch (e) {
      // 409 che non è «email_mancante»: la pagina non sapeva delle letture in corso, o l'indirizzo è cambiato dopo che
      // l'ha letto (`email_cambiata`, la mail non è partita): si ricarica e il pannello dice da sé il perché, o mostra il nuovo indirizzo
      if (e instanceof ApiError && e.status === 409 && e.detail !== "email_mancante") onRifiutato();
      else setErrore(canale);
    } finally {
      inVolo.current = false;
      setInCorso(null);
    }
  }

  const icona = (canale: Canale, Normale: typeof Mail) => (
    inCorso === canale ? <LoaderCircle size={18} aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
      : <Normale size={18} aria-hidden="true" />
  );
  const tutti = scelti.length === elenco.length;

  const piede = (
    <div className="space-y-2">
      {mancano > 0 && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock size={16} aria-hidden="true" className="shrink-0" />
          {ancoraInLettura(mancano)}
        </p>
      )}
      {bloccato && <p className="text-sm font-medium text-text">{PRIMA_SALVA}</p>}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => { void manda("mail"); }}
          disabled={!pronto || email === null}
          aria-busy={inCorso === "mail"}
          className={PRIMARIO}
        >
          {icona("mail", Mail)}
          {MAIL}
        </button>
        <button
          type="button"
          onClick={() => { void manda("whatsapp"); }}
          disabled={!pronto}
          aria-busy={inCorso === "whatsapp"}
          className={SECONDARIO}
        >
          {icona("whatsapp", MessageCircle)}
          {WHATSAPP}
        </button>
      </div>
      <p className="text-center text-xs text-muted-foreground break-all">{email === null ? MANCA_EMAIL : laMailVaA(email)}</p>
      {errore === "mail" && <AvvisoErrore testo={MAIL_NON_PARTITA} onRiprova={() => { void manda("mail"); }} />}
      {errore === "whatsapp" && <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} onRiprova={() => { void manda("whatsapp"); }} />}
      {ultimo && (
        <p className="flex items-start justify-center gap-1.5 text-xs text-muted-foreground">
          {ultimo.canale === "mail" ? <Mail size={14} aria-hidden="true" className="mt-px shrink-0" /> : <MessageCircle size={14} aria-hidden="true" className="mt-px shrink-0" />}
          <span className="min-w-0 break-words">{rigaInvio(ultimo)}</span>
        </p>
      )}
    </div>
  );

  return (
    <PannelloBasso
      aperto={aperto}
      onChiudi={onChiudi}
      titolo={INVIA_AMMINISTRATORE}
      sopra={vista.amministratore?.nome?.trim() || undefined}
      piede={piede}
      centrato
    >
      <fieldset>
        <legend className="sr-only">{condominiNelLink(scelti.length, elenco.length)}</legend>
        {/* il conto e «Togli tutti» restano in vista mentre la lista scorre */}
        <div className="sticky top-0 z-10 mt-3 flex min-h-11 items-center justify-between gap-3 border-b border-border bg-card">
          <p aria-hidden="true" className="text-sm font-semibold text-text">{condominiNelLink(scelti.length, elenco.length)}</p>
          <button
            type="button"
            onClick={() => onTutti(!tutti)}
            className={`-mr-2 h-11 shrink-0 rounded-lg px-2 text-sm font-medium text-brand-dark cursor-pointer touch-manipulation hover:bg-surface ${FOCUS}`}
          >
            {tutti ? TOGLI_TUTTI : METTI_TUTTI}
          </button>
        </div>
        <ul className="divide-y divide-border">
          {elenco.map((c) => (
            <li key={c.chiave}>
              <label className="flex min-h-12 cursor-pointer items-center gap-3 py-2">
                <input
                  type="checkbox"
                  checked={!tolti.has(c.chiave)}
                  onChange={() => onScambia(c.chiave)}
                  className={`size-5 shrink-0 cursor-pointer accent-brand-dark ${FOCUS}`}
                />
                {/* il numero di forniture su una riga sua: un nome lungo che va a capo non lascia il «·» appeso */}
                <span className="min-w-0 break-words text-sm text-text">
                  <span className="block font-medium">{c.titolo}</span>
                  <span className="block text-xs text-muted-foreground">{fornitureN(c.n_forniture)}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      {/* il registro intero solo con più invii: l'ultimo è già scritto sotto i tasti */}
      {invii.length > 1 && <div className="mt-2 mb-4"><Invii invii={invii} /></div>}
    </PannelloBasso>
  );
}
